import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import type { UserRole } from '../types/auth';
import { hasMinimumRole, getDashboardUrl } from '../lib/permissionService';

/**
 * Props for ProtectedRoute component
 */
interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: UserRole;
  allowedRoles?: UserRole[];
  fallbackUrl?: string;
}

/**
 * ProtectedRoute Component
 *
 * Wraps route elements that require authentication and/or specific roles.
 *
 * Usage:
 * ```tsx
 * <Route
 *   path="/admin"
 *   element={
 *     <ProtectedRoute requiredRole="ADMIN">
 *       <AdminPage />
 *     </ProtectedRoute>
 *   }
 * />
 *
 * <Route
 *   path="/host"
 *   element={
 *     <ProtectedRoute allowedRoles={['HOST', 'ADMIN', 'SUPERADMIN']}>
 *       <HostPage />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 *
 * When user lacks required role:
 * - Logged in: Redirects to 403 Forbidden page
 * - Not logged in: Redirects to login
 */
export const ProtectedRoute = ({
  children,
  requiredRole,
  allowedRoles,
  fallbackUrl,
}: ProtectedRouteProps) => {
  const { user, token } = useAuthStore();

  // Check if user is authenticated
  if (!token || !user) {
    // Not authenticated - redirect to landing page to login
    return <Navigate to="/" replace />;
  }

  // Check if user has required role
  const hasRequiredRole =
    requiredRole && hasMinimumRole(user.role, requiredRole);
  const hasAllowedRole =
    !allowedRoles ||
    allowedRoles.includes(user.role);

  // Determine if user has access
  const hasAccess = requiredRole ? hasRequiredRole : hasAllowedRole;

  if (!hasAccess) {
    // User is authenticated but doesn't have required role
    const redirectUrl = fallbackUrl || '/forbidden';
    return <Navigate to={redirectUrl} replace />;
  }

  // User has access - render children
  return <>{children}</>;
};

export default ProtectedRoute;
